/**
 * LeagueScene ('League') — the Summit: Elite Four + Champion (Round 3, elite-four.md).
 *
 * A chained GymScene: the warm anteroom pattern, extended into a 5-bout gauntlet with
 * EVERY gauntlet-stressor removed. You fight Elite 1 → 2 → 3 → 4 → Champion back-to-back,
 * but the team is FULLY HEALED between every bout, you may step out after any bout and
 * resume exactly where you left off, and a loss costs nothing (you resume the SAME bout,
 * healed, whenever you like). Beating the Champion writes a Hall of Fame page, drops a
 * laurel over the square, and the whole town cheers.
 *
 * The renderer owns no rules: parties come from data/quests/league via society/makeInstance
 * (buildMemberParty), the outcome comes from the pure engine, and all resume/heal/HoF state
 * lives on the session (leagueState / startLeagueRun / advanceLeagueBout). Re-entry safe:
 * Phaser REUSES this instance, so create() re-runs on every visit (League → Battle → League)
 * — resetState()/teardown() drop all prior display + flags so nothing stacks or soft-locks.
 */
import Phaser from 'phaser';
import { COLORS, CSS, FONT, RADIUS, SPACING } from './ui/theme.js';
import { DialogBox } from './ui/DialogBox.js';
import { Menu, type MenuItem } from './ui/Menu.js';
import { Layout } from './layout/index.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import { getSession } from './session.js';
import type { GameSession } from '@/session';
import { buildMemberParty } from '@/session/league.js';
import type { LeagueMember } from '@/data/quests/league.js';

/** How the battle result rides back into this scene (see BattleScene.endBattle). */
type LeagueResult = 'pending' | 'won' | 'lost';

/** Launch/return payload for the League scene. */
interface LeagueSceneData {
  /** Set true on every League→Battle hand-off so the return trip is recognised. */
  readonly league?: boolean;
  /** Set by BattleScene on return via the gymResult rewrite: 'won' | 'lost'. */
  readonly gymResult?: LeagueResult;
}

export class LeagueScene extends Phaser.Scene {
  private layout!: Layout;
  private reduced = false;
  private session!: GameSession;

  private isReturn = false;
  private pendingResult: LeagueResult | undefined;

  private heading?: Phaser.GameObjects.Text;
  private subheading?: Phaser.GameObjects.Text;
  private counter?: Phaser.GameObjects.Text;
  private emblem?: Phaser.GameObjects.Graphics;
  private backdrop?: Phaser.GameObjects.Graphics;
  private dialog?: DialogBox;
  private menu?: Menu;
  private afterDialog?: () => void;
  private busy = false;

  constructor() {
    super('League');
  }

  init(data: Partial<LeagueSceneData>): void {
    this.isReturn = data.league === true;
    this.pendingResult = data.gymResult;
  }

  create(): void {
    this.resetState();
    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);
    this.reduced = isReducedMotion();
    this.session = getSession(this);

    this.backdrop = this.add.graphics();
    this.emblem = this.add.graphics();

    this.heading = this.add
      .text(0, 0, 'The Summit', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.subheading = this.add
      .text(0, 0, 'The League hall, warm with lantern-light', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);
    this.counter = this.add
      .text(0, 0, '', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);

    this.dialog = new DialogBox(this, 0, 0, {
      width: 340,
      height: 100,
      reducedMotion: this.reduced,
      onDone: () => {
        const fn = this.afterDialog;
        this.afterDialog = undefined;
        if (this.scene.isActive()) fn?.();
      },
    });
    this.dialog.setVisible(false);

    this.relayout();
    this.layout.on('change', () => this.relayout());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());

    this.route();
    fadeIn(this);
  }

  // ── flow router ─────────────────────────────────────────────────────────────

  /** Decide what to show on this entry: a battle return, a resume, or the entry offer. */
  private route(): void {
    if (this.isReturn && this.pendingResult === 'won') {
      void this.onVictory();
      return;
    }
    if (this.isReturn && this.pendingResult === 'lost') {
      this.onDefeat();
      return;
    }
    // A fresh entry (from the RegionMap). Resume an underway run, else offer to enter.
    if (this.session.leagueState.inProgress) {
      this.updateCounter();
      const member = this.session.leagueMemberForBout;
      if (member) {
        this.showLines(
          ['Welcome back to the Summit. Your team is rested and ready when you are.'],
          'The League',
          () => this.anteroom(member),
        );
      } else {
        // Defensive: inProgress but past the last bout — treat as a clean re-entry.
        this.onEntryOffer();
      }
    } else {
      this.onEntryOffer();
    }
  }

  // ── anteroom phases ───────────────────────────────────────────────────────────

  /** First visit (or a rematch): the hall greets you, then Enter / Not today. */
  private onEntryOffer(): void {
    const cleared = this.session.leagueState.clears > 0;
    const lines = cleared
      ? [
          'The Summit doors stand open for you always, Champion.',
          'Fancy another warm run through the hall? Elite 1 will be delighted to see you.',
        ]
      : [
          'You made it — all eight lanterns lit, and the Summit doors swung wide for you.',
          'Four warm friends and a Champion wait beyond, with a full rest between each.',
          'No clock, no catch — step out any time and pick up right where you left off.',
        ];
    this.showLines(lines, 'The League', () => {
      this.showMenu([
        { label: 'Enter the hall', onSelect: () => void this.enterHall() },
        { label: 'Not today', onSelect: () => this.goHome() },
      ]);
    });
  }

  private async enterHall(): Promise<void> {
    if (this.busy) return;
    this.hideMenu();
    await this.session.startLeagueRun();
    if (!this.scene.isActive()) return;
    this.updateCounter();
    const member = this.session.leagueMemberForBout;
    if (member) this.anteroom(member);
    else this.goHome();
  }

  /** Present the current bout's member (name + title), then Battle / Step out. */
  private anteroom(member: LeagueMember): void {
    this.updateCounter();
    this.showLines(member.intro, `${member.name} · ${member.title}`, () => {
      this.showMenu([
        { label: 'Battle!', onSelect: () => this.startBattle(member) },
        { label: 'Step out (resume later)', onSelect: () => this.goHome() },
      ]);
    });
  }

  private async onVictory(): Promise<void> {
    // The member just fought is still the current bout (advance happens below).
    const member = this.session.leagueMemberForBout;
    if (!member) {
      this.goHome();
      return;
    }
    // Play their warm onWin lines, THEN fold the win and branch on the result.
    this.showLines(member.onWin, member.name, () => {
      void this.resolveWin(member);
    });
  }

  private async resolveWin(member: LeagueMember): Promise<void> {
    const result = await this.session.advanceLeagueBout(true);
    if (!this.scene.isActive()) return;
    if (result === 'cleared') {
      this.onHallOfFame(member);
      return;
    }
    if (result === 'next') {
      const next = this.session.leagueMemberForBout;
      this.updateCounter();
      // The heal is VISIBLE + reassuring — the no-attrition promise, felt.
      this.showLines(['✦ Your team is fully rested. ♥'], 'The League', () => {
        if (next) this.anteroom(next);
        else this.goHome();
      });
      return;
    }
    // 'stay' shouldn't happen after a win, but keep a warm path home.
    this.goHome();
  }

  private onDefeat(): void {
    const member = this.session.leagueMemberForBout;
    // A loss is no-stakes: warm words, no regression. Resume the SAME bout, healed.
    void this.session.advanceLeagueBout(false);
    const lines = member?.onLose ?? [
      'Your team’s worn out — let’s rest. The hall will be right here whenever you like.',
    ];
    this.showLines(lines, member?.name ?? 'The League', () => {
      this.showMenu([
        {
          label: 'Rest & try again',
          onSelect: () => {
            const m = this.session.leagueMemberForBout;
            if (m) this.anteroom(m);
            else this.goHome();
          },
        },
        { label: 'Head back', onSelect: () => this.goHome() },
      ]);
    });
  }

  /** The Champion is beaten: a gentle flourish, the team snapshot, then home to cheers. */
  private onHallOfFame(champion: LeagueMember): void {
    const hof = this.session.leagueState.hallOfFame;
    const latest = hof[hof.length - 1];
    const roster =
      latest?.team.map((m) => `  ${m.nickname ?? m.speciesId} · Lv ${m.level}`).join('\n') ?? '';
    const pages = [
      `✦ Champion ✦\n${this.session.trainerName}, and the team that walked the whole circuit beside you.`,
      roster.length > 0 ? roster : 'A team that will be remembered.',
      'A laurel is raised over the town square. The whole town is cheering for you.',
    ];
    // A quiet gold wash behind the flourish (reused emblem motif), then home.
    this.showLines(pages, `${champion.name} · Champion of the region`, () => this.goHome());
  }

  // ── the hand-off to the shared Battle scene ─────────────────────────────────────

  private startBattle(member: LeagueMember): void {
    if (this.busy) return;
    const party = buildMemberParty(this.session.bundle.index, this.session.seed, member);
    if (party.length === 0) {
      this.showLines(
        [`${member.name}: my team’s resting just now — come spar another day!`],
        member.name,
        () => this.goHome(),
      );
      return;
    }
    this.busy = true;
    this.hideMenu();
    this.dialog?.setVisible(false);
    goTo(this, 'Battle', {
      data: {
        kind: 'friendly',
        opponentParty: party,
        opponentName: `${member.name}, ${member.title}`,
        returnScene: 'League',
        // gymId here is ONLY the endBattle result-rewrite hook (it fires no quest event);
        // `league: true` marks the return trip. leagueResult rides back as `gymResult`.
        returnData: { league: true, gymId: member.eliteId, gymResult: 'pending' as LeagueResult },
        // Respect the player's Cozy↔Spicy dial, exactly like GymScene.
        difficultyOverride: this.session.currentSettings.difficulty,
      },
    });
  }

  // ── dialog + menu helpers ───────────────────────────────────────────────────────

  private updateCounter(): void {
    const bout = this.session.leagueState.boutIndex;
    // Glanceable "Bout n of 5" — never a countdown-timer, just a gentle place-marker.
    this.counter?.setText(bout < 5 ? `Bout ${bout + 1} of 5` : '');
  }

  private showLines(lines: readonly string[], speaker: string, after: () => void): void {
    this.afterDialog = after;
    const pages = lines.length > 0 ? lines : ['…'];
    this.dialog?.show(pages, speaker);
  }

  private showMenu(items: readonly MenuItem[]): void {
    this.hideMenu();
    const menu = new Menu(this, 0, 0, {
      items,
      reducedMotion: this.reduced,
      width: Math.min(this.layout.safe.width - SPACING.md * 2, 320),
      onCancel: () => this.goHome(),
    });
    this.menu = menu;
    this.positionMenu();
    menu.focus();
  }

  private hideMenu(): void {
    this.menu?.destroy();
    this.menu = undefined;
  }

  private goHome(): void {
    if (this.busy) return;
    this.busy = true;
    this.hideMenu();
    goTo(this, 'RegionMap');
  }

  // ── layout ──────────────────────────────────────────────────────────────────────

  private relayout(): void {
    const safe = this.layout.safe;
    const cx = this.layout.centerX;
    const top = safe.y + SPACING.xl;

    this.drawBackdrop();

    this.heading?.setPosition(cx, top);
    this.subheading?.setPosition(cx, top + FONT.size.lg + SPACING.sm);
    this.counter?.setPosition(cx, top + FONT.size.lg + SPACING.sm + FONT.size.sm + SPACING.xs);
    this.drawEmblem(cx, top + FONT.size.lg + SPACING.sm + FONT.size.sm + SPACING.xl + SPACING.md);

    const dialogH = 100;
    const dialogY = safe.y + safe.height - dialogH / 2 - SPACING.md;
    this.dialog?.setPosition(cx, dialogY);
    this.positionMenu();
  }

  private positionMenu(): void {
    if (!this.menu) return;
    const safe = this.layout.safe;
    const dialogH = 100;
    const dialogTop = safe.y + safe.height - dialogH - SPACING.md;
    this.menu.setPosition(this.layout.centerX, dialogTop - this.menu.boxHeight / 2 - SPACING.sm);
  }

  private drawBackdrop(): void {
    if (!this.backdrop) return;
    const safe = this.layout.safe;
    const g = this.backdrop;
    g.clear();
    g.fillStyle(COLORS.surface, 1);
    g.fillRect(safe.x, safe.y, safe.width, safe.height);
    // A warm central dais — the throne-hall floor (placeholder cozy room, MVP).
    g.fillStyle(COLORS.surfaceAlt, 0.55);
    g.fillRoundedRect(
      safe.x + SPACING.md,
      safe.y + safe.height * 0.3,
      safe.width - SPACING.md * 2,
      safe.height * 0.36,
      RADIUS.lg,
    );
  }

  /** A gold laurel-ish emblem — a gentle nod to the Champion's crown. */
  private drawEmblem(x: number, y: number): void {
    if (!this.emblem) return;
    const g = this.emblem;
    g.clear();
    const r = 28;
    g.fillStyle(COLORS.scrim, 0.2);
    g.fillCircle(x, y + 3, r);
    g.fillStyle(COLORS.warm, 1);
    g.fillCircle(x, y, r);
    g.lineStyle(3, COLORS.boxBorder, 1);
    g.strokeCircle(x, y, r);
    g.fillStyle(COLORS.boxFill, 0.9);
    g.fillCircle(x, y, r * 0.4);
  }

  // ── re-entry safety ──────────────────────────────────────────────────────────────

  private resetState(): void {
    this.heading = undefined;
    this.subheading = undefined;
    this.counter = undefined;
    this.emblem = undefined;
    this.backdrop = undefined;
    this.dialog = undefined;
    this.menu = undefined;
    this.afterDialog = undefined;
    this.busy = false;
  }

  private teardown(): void {
    this.tweens.killAll();
    this.hideMenu();
    this.dialog?.destroy();
    this.heading?.destroy();
    this.subheading?.destroy();
    this.counter?.destroy();
    this.emblem?.destroy();
    this.backdrop?.destroy();
    this.dialog = undefined;
    this.heading = undefined;
    this.subheading = undefined;
    this.counter = undefined;
    this.emblem = undefined;
    this.backdrop = undefined;
    this.afterDialog = undefined;
  }
}
